import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  redirect,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useAccountDraftGuard } from "./auth/AccountWorkspaceActions";
import { useClinicalRuntime } from "./auth/ClinicalRuntimeProvider";
import { AdminSectionLoading } from "./components/AdminSectionHeader";
import { AppShell } from "./components/AppShell";
import { RouteLoading } from "./components/RouteLoading";
import { DemoNoteTemplatesProvider } from "./demo/DemoNoteTemplatesProvider";
import { DemoSchedulingProvider } from "./demo/DemoSchedulingProvider";
import { DemoWorkspaceProvider } from "./demo/DemoWorkspaceProvider";
import { demoSchedulingClient } from "./demo/demoSchedulingClient";
import { createPersistedSchedulingClient } from "./demo/persistedSchedulingClient";
import { validateClinicRulesSearch, validateFormSetupSearch } from "./forms/ui/useFormStep";
import { validateAttentionSearch } from "./lib/attentionSearch";
import { validateScheduleSearch } from "./lib/scheduleSearch";
import { useWorkforceAccount } from "./lib/workforceAccount";
import {
  patientMessagesSearch,
  validatePatientMessagesSearch,
} from "./pages/admin/patientMessagesSections";
import { ProfileSectionKeySchema } from "./pages/patient-profile/profileSections";
import {
  scheduleSettingsSearch,
  validateScheduleSettingsSearch,
} from "./pages/schedule/settings/scheduleSettingsSections";
import { TodayPage } from "./pages/TodayPage";
import { EncounterSelectionProvider, useEncounterSelection } from "./state/encounterSelection";
import { PatientProfileTabProvider, usePatientProfileTab } from "./state/patientProfileTab";
import { SignedNoteTabsProvider, useSignedNoteTabs } from "./state/signedNoteTabs";

const SignedNotePage = lazy(() => import("./pages/SignedNotePage"));
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
const EmrPageImportPage = lazy(() => import("./pages/EmrPageImportPage"));
const AttentionPage = lazy(() => import("./pages/AttentionPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const AdminSettingsLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverviewPage = lazy(() => import("./pages/admin/AdminOverviewPage"));
const AdminPeoplePage = lazy(() => import("./pages/admin/AdminPeoplePage"));
const AdminContactPage = lazy(() => import("./pages/admin/AdminContactPage"));
const AdminMessagingPage = lazy(() => import("./pages/admin/AdminMessagingPage"));
const AdminPatientTermsPage = lazy(() => import("./pages/admin/AdminPatientTermsPage"));
const AdminPatientMessagesPage = lazy(() => import("./pages/admin/AdminPatientMessagesPage"));
const AdminEmrImportPage = lazy(() => import("./pages/admin/AdminEmrImportPage"));
const SchedulePage = lazy(() => import("./pages/SchedulePage"));
const ScheduleSettingsPage = lazy(() => import("./pages/ScheduleSettingsPage"));
const TemplateSettingsPage = lazy(() => import("./pages/TemplateSettingsPage"));
const ReferralSettingsPage = lazy(() => import("./pages/ReferralSettingsPage"));
const ReferralDirectoryPage = lazy(() => import("./pages/ReferralDirectoryPage"));
const FormsLibraryPage = lazy(() => import("./pages/FormsLibraryPage"));
const FormsWorkspacePage = lazy(() => import("./pages/FormsWorkspacePage"));
const FormsInstructionsPage = lazy(() => import("./pages/FormsInstructionsPage"));
const FormsTestNamesPage = lazy(() => import("./pages/FormsTestNamesPage"));
const ProviderSignaturePage = lazy(() => import("./pages/ProviderSignaturePage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));

interface RouterContext {
  queryClient: QueryClient;
}

function ClinicalRoot() {
  const runtime = useClinicalRuntime();
  const schedulingClient = useMemo(
    () =>
      import.meta.env.VITE_DEMO_SCHEDULING_ENABLED === "true"
        ? demoSchedulingClient
        : createPersistedSchedulingClient({
            baseUrl: runtime.config.agentApiOrigin,
            siteId: runtime.config.siteId,
            getAccessToken: runtime.workforce.accessToken,
          }),
    [runtime.config.agentApiOrigin, runtime.config.siteId, runtime.workforce.accessToken],
  );
  return (
    <DemoSchedulingProvider key={runtime.config.siteId} client={schedulingClient}>
      <DemoNoteTemplatesProvider>
        <DemoWorkspaceProvider>
          <EncounterSelectionProvider>
            <PatientProfileTabProvider>
              <SignedNoteTabsProvider>
                <AppShell />
              </SignedNoteTabsProvider>
            </PatientProfileTabProvider>
          </EncounterSelectionProvider>
        </DemoWorkspaceProvider>
      </DemoNoteTemplatesProvider>
    </DemoSchedulingProvider>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: ClinicalRoot,
  notFoundComponent: () => (
    <main className="empty-route">
      <span className="eyebrow">404</span>
      <h1>This workspace does not exist.</h1>
      <p>Return to the Today board to continue clinic work.</p>
    </main>
  ),
});

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: TodayPage,
});

function EncounterRouteComponent() {
  const { selectedEncounter, encounterTabs, selectEncounter } = useEncounterSelection();
  const { tab } = encounterRoute.useSearch();
  const navigate = useNavigate();
  const selectionRef = useRef({ selectedEncounter, encounterTabs, selectEncounter });
  selectionRef.current = { selectedEncounter, encounterTabs, selectEncounter };
  useEffect(() => {
    // Only browser navigation selects a history entry. Selecting/closing a tab
    // updates state just before navigation and must not restore the old URL.
    const { selectedEncounter, encounterTabs, selectEncounter } = selectionRef.current;
    if (tab) {
      const target = encounterTabs.find((candidate) => candidate.tabId === tab);
      if (!target) {
        void navigate({ to: "/", replace: true });
        return;
      }
      if (target.encounterId !== selectedEncounter?.encounterId) selectEncounter(target);
    } else if (selectedEncounter) {
      void navigate({ to: "/encounter", search: { tab: selectedEncounter.tabId }, replace: true });
    }
  }, [tab, navigate]);
  if (!selectedEncounter) {
    return (
      <main className="empty-route" aria-labelledby="no-encounter-title">
        <span className="eyebrow">Session workspace</span>
        <h1 id="no-encounter-title">Choose an encounter from Schedule or Today.</h1>
        <p>Patient context is kept in memory and is never encoded in a link or browser history.</p>
      </main>
    );
  }

  // The encounter surface is owned by AppShell's keep-alive tab pane so it
  // survives tab switches; this route contributes only the empty state above.
  return null;
}

const encounterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/encounter",
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    ...(typeof search.tab === "string" && /^[0-9a-f]{32}$/i.test(search.tab)
      ? { tab: search.tab }
      : {}),
  }),
  component: EncounterRouteComponent,
});

// Only a random, session-local tab handle enters the URL. The signed note
// and encounter identifiers stay in memory; a refresh cannot restore its text.
function SignedNoteRouteComponent() {
  const { tabId } = signedNoteRoute.useParams();
  const { tabs } = useSignedNoteTabs();
  const tab = tabs.find((candidate) => candidate.id === tabId);
  if (!tab) {
    return (
      <main className="empty-route">
        <h1>This note tab is closed.</h1>
        <p>Open the signed note again from the patient's visit history.</p>
      </main>
    );
  }
  return (
    <Suspense fallback={<RouteLoading label="Opening signed note" />}>
      <SignedNotePage tab={tab} />
    </Suspense>
  );
}

const signedNoteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/visit-note/$tabId",
  component: SignedNoteRouteComponent,
});

const patientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/patients",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading patient charts" />}>
      <PatientsPage />
    </Suspense>
  ),
});

// Where the "Send to BobDoc" bookmark lands from another EMR's chart page
// (EXECDEV-227). The chart arrives by postMessage, never in the URL.
const emrPageImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/import/emr-page",
  component: () => (
    <Suspense fallback={<RouteLoading label="Opening the import" />}>
      <EmrPageImportPage />
    </Suspense>
  ),
});

// The patient profile is the one patient-scoped URL (owner decision
// 2026-09-03): the opaque patient UUID is not PHI, and a deep link that
// reloads and shares is worth more than hiding it. The PHI-free invariant
// still holds for everything else — names, MRNs and health numbers never
// enter a URL, and encounter context stays in memory (see /encounter above).
function PatientProfileRouteComponent() {
  const { patientId } = patientProfileRoute.useParams();
  const { section } = patientProfileRoute.useSearch();
  const { openProfileTab } = usePatientProfileTab();
  // The URL selects a patient workspace; deep links, back/forward, and the
  // directory reuse that patient's tab while other profiles stay open.
  useEffect(() => {
    openProfileTab(patientId, { section });
  }, [openProfileTab, patientId, section]);

  // The shell owns the profile in a keep-alive tab pane, exactly as it owns
  // the encounter, so this route contributes nothing.
  return null;
}

const patientProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/patients/$patientId",
  validateSearch: (search: Record<string, unknown>) => ({
    section: ProfileSectionKeySchema.catch("overview").parse(search.section),
  }),
  component: PatientProfileRouteComponent,
});

// A Schedule link selects one booking by its opaque id, and may open its texts and emails
// (PA-7). The page drops the parameters once it has applied them.
function ScheduleRouteComponent() {
  const selection = scheduleRoute.useSearch();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<RouteLoading label="Loading the shared clinic calendar" />}>
      <SchedulePage
        selection={selection}
        onSelectionHandled={() => void navigate({ to: "/schedule", search: {}, replace: true })}
      />
    </Suspense>
  );
}

const scheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/schedule",
  validateSearch: validateScheduleSearch,
  component: ScheduleRouteComponent,
});

const attentionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/attention",
  validateSearch: validateAttentionSearch,
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading attention inbox" />}>
      <AttentionPage />
    </Suspense>
  ),
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading billing workbench" />}>
      <BillingPage />
    </Suspense>
  ),
});

// The admin area: one shell (heading plus the left menu of sections) around a content
// column, and one route per section. Forms and the staff profile keep their own full
// pages (they are workspaces with their own chrome) and sit outside the shell.
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin-settings",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading clinic settings" />}>
      <AdminSettingsLayout />
    </Suspense>
  ),
});

const adminRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading clinic settings…" />}>
      <AdminOverviewPage />
    </Suspense>
  ),
});

const adminPeopleRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/people",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading people and roles…" />}>
      <AdminPeoplePage />
    </Suspense>
  ),
});

const adminContactRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/contact",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading clinic contact details…" />}>
      <AdminContactPage />
    </Suspense>
  ),
});

const adminMessagingRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/messaging",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading clinic messaging settings…" />}>
      <AdminMessagingPage />
    </Suspense>
  ),
});

const adminPatientTermsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/patient-terms",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading the patient terms…" />}>
      <AdminPatientTermsPage />
    </Suspense>
  ),
});

const adminEmrImportRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/emr-import",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading the import setting…" />}>
      <AdminEmrImportPage />
    </Suspense>
  ),
});

// Patient messages and Scheduling each have a sub-menu: the group is ?section=, pushed so
// back/forward step through the groups (replaced when the page corrects a group it can't show).
function AdminPatientMessagesRouteComponent() {
  const { section } = adminPatientMessagesRoute.useSearch();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<AdminSectionLoading label="Loading the clinic's message settings…" />}>
      <AdminPatientMessagesPage
        {...(section === undefined ? {} : { section })}
        onSectionChange={(next, options) =>
          void navigate({
            to: "/admin/patient-messages",
            search: patientMessagesSearch(next),
            resetScroll: false,
            replace: options?.replace === true,
          })
        }
      />
    </Suspense>
  );
}

const adminPatientMessagesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/patient-messages",
  validateSearch: validatePatientMessagesSearch,
  component: AdminPatientMessagesRouteComponent,
});

function ScheduleSettingsRouteComponent() {
  const { section } = scheduleSettingsRoute.useSearch();
  const navigate = useNavigate();
  return (
    <Suspense fallback={<AdminSectionLoading label="Loading scheduling configuration…" />}>
      <ScheduleSettingsPage
        {...(section === undefined ? {} : { section })}
        onSectionChange={(next, options) =>
          void navigate({
            to: "/admin/scheduling",
            search: scheduleSettingsSearch(next),
            resetScroll: false,
            replace: options?.replace === true,
          })
        }
      />
    </Suspense>
  );
}

const scheduleSettingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/scheduling",
  validateSearch: validateScheduleSettingsSearch,
  component: ScheduleSettingsRouteComponent,
});

const templateSettingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/templates",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading the template library…" />}>
      <TemplateSettingsPage />
    </Suspense>
  ),
});

const referralSettingsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/referrals",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading referral policy…" />}>
      <ReferralSettingsPage />
    </Suspense>
  ),
});

const referralDirectoryRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/referrals/directory",
  component: () => (
    <Suspense fallback={<AdminSectionLoading label="Loading the referral directory…" />}>
      <ReferralDirectoryPage />
    </Suspense>
  ),
});

const adminSettingsRoute = adminLayoutRoute.addChildren([
  adminRoute,
  adminPeopleRoute,
  adminContactRoute,
  adminMessagingRoute,
  adminPatientTermsRoute,
  adminPatientMessagesRoute,
  adminEmrImportRoute,
  scheduleSettingsRoute,
  templateSettingsRoute,
  referralSettingsRoute,
  referralDirectoryRoute,
]);

const documentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents",
  beforeLoad: () => {
    throw redirect({ to: "/admin/forms" });
  },
});

// Static segment outranks the dynamic sibling, so /documents/rules never
// resolves as a documentId.
const documentRulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents/rules",
  beforeLoad: () => {
    throw redirect({ to: "/admin/forms/instructions" });
  },
});

function FormsWorkspaceRouteComponent() {
  const { formId } = formsWorkspaceRoute.useParams();
  return (
    <Suspense fallback={<RouteLoading label="Loading the form" />}>
      <FormsWorkspacePage formId={formId} />
    </Suspense>
  );
}

const documentWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/documents/$documentId",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/admin/forms/$formId", params: { formId: params.documentId } });
  },
});

const formsLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/forms",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading forms" />}>
      <FormsLibraryPage />
    </Suspense>
  ),
});
const formsInstructionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/forms/instructions",
  validateSearch: validateClinicRulesSearch,
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading clinic rules" />}>
      <FormsInstructionsPage />
    </Suspense>
  ),
});
// Static segments outrank the dynamic $formId sibling.
const formsTestNamesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/forms/test-names",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading test names" />}>
      <FormsTestNamesPage />
    </Suspense>
  ),
});
const formsWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/forms/$formId",
  validateSearch: validateFormSetupSearch,
  component: FormsWorkspaceRouteComponent,
});

// The vault predates the top-level Documents workspace; saved links keep
// working through this permanent redirect.
const legacyClinicDocumentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/documents",
  beforeLoad: () => {
    throw redirect({ to: "/admin/forms" });
  },
});

const providerSignatureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/signature",
  beforeLoad: () => {
    throw redirect({ to: "/account/signature" });
  },
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account",
  component: () => (
    <Suspense fallback={<RouteLoading label="Loading your profile" />}>
      <AccountPage />
    </Suspense>
  ),
});
const accountSignatureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/account/signature",
  component: AccountSignatureRoute,
});
function AccountSignatureRoute() {
  const account = useWorkforceAccount();
  const [dirty, setDirty] = useState(false);
  useAccountDraftGuard(dirty);
  useBlocker({
    shouldBlockFn: () => dirty && !window.confirm("Discard your unfinished signature change?"),
    enableBeforeUnload: dirty,
  });
  return (
    <>
      <Link className="secondary-button" to="/account">
        Back to my profile
      </Link>
      {account.isPending ? (
        <RouteLoading label="Loading your signature access" />
      ) : account.isError ? (
        <p role="alert">Signature access could not be checked. Return to your profile and retry.</p>
      ) : account.data?.role !== "physician" ? (
        <main className="page">
          <h1>Provider signature</h1>
          <p>Signature enrollment is available to physicians.</p>
        </main>
      ) : (
        <Suspense fallback={<RouteLoading label="Loading your signature" />}>
          <ProviderSignaturePage onDirtyChange={setDirty} />
        </Suspense>
      )}
    </>
  );
}
function StaffProfileRoute() {
  const { userId } = staffProfileRoute.useParams();
  return (
    <Suspense fallback={<RouteLoading label="Loading staff profile" />}>
      <AccountPage userId={userId} />
    </Suspense>
  );
}
const staffProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/people/$userId",
  component: StaffProfileRoute,
});

const routeTree = rootRoute.addChildren([
  todayRoute,
  patientsRoute,
  emrPageImportRoute,
  patientProfileRoute,
  scheduleRoute,
  encounterRoute,
  signedNoteRoute,
  attentionRoute,
  billingRoute,
  documentsRoute,
  formsLibraryRoute,
  formsInstructionsRoute,
  formsTestNamesRoute,
  formsWorkspaceRoute,
  documentRulesRoute,
  documentWorkspaceRoute,
  adminSettingsRoute,
  legacyClinicDocumentsRoute,
  providerSignatureRoute,
  accountRoute,
  accountSignatureRoute,
  staffProfileRoute,
]);

export const router = createRouter({
  routeTree,
  context: { queryClient: undefined as unknown as QueryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
